import { useMemo } from 'react';
import ResponsiveTable from '../../../../shared/components/ResponsiveTable';
import {
  HeaderPropSortCallback,
  SortedColumn,
} from '../../../../shared/components/ResponsiveTable/models';
import { PMIFormatDate } from '../../../../shared/helpers';
import { ProductUsers } from '../../../../shared/models/ProductUsersModel';
import styles from '../productDetailsReport.module.scss';
import { ProductTypes } from '../../../../shared/constants';
import OptedOutChip from '../../../../shared/components/OptedOutChip';

export interface RegisteredUsersTableProps {
  list: ProductUsers[];
  page: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  onSort: HeaderPropSortCallback;
  sortedColumn: SortedColumn;
  type: string;
  isInternalAdmin: boolean;
}

const nameCell = (sortedColumn: SortedColumn, isInternalAdmin: boolean) => ({
  name: 'firstName',
  title: 'Name',
  cell: (cntx: any) => (
    <span>
      <span className="fw-bold" data-testid="data-name" data-exclude-lg>
        {cntx.row.original.firstName} {cntx.row.original.lastName}
      </span>
      <br />
      <span
        className={`${styles['grey-3']} label text-break`}
        data-testid="data-email"
      >
        {cntx.row.original.email}
      </span>
      {isInternalAdmin && (
        <>
          <br />
          <span className={`${styles['grey-3']} label`} data-testid="data-id">
            {`Person ID: ${
              // if personId is equal to single zero, it means it's been anonymized and should set it to 7 zeros to follow the convention
              cntx.row.original.personId === '0'
                ? '0000000'
                : cntx.row.original.personId
            } `}
          </span>
        </>
      )}
      <OptedOutChip
        isInternalAdmin={isInternalAdmin}
        shareData={cntx.row.original.shareData}
      />
    </span>
  ),
  sortingEnabled: true,
  width: 15,
  direction: sortedColumn?.name === 'firstName' ? sortedColumn.direction : null,
});

const orderIdCell = (): SortedColumn => ({
  name: 'orderIncrementId',
  title: 'Order ID',
  cell: (cntx: any) => (
    <span
      className={`${styles['grey-3']} label text-break`}
      data-testid="data-order-id"
    >
      {cntx.row.original.orderIncrementId}
    </span>
  ),
  sortingEnabled: false,
  width: 10,
  direction: undefined,
});

const secondaryStatusCell = (sortedColumn: SortedColumn, type: string) => ({
  name: 'secondaryStatus',
  title: `${type === ProductTypes.onlineCourse ? 'Course Status' : 'Status'}`,
  cell: (cntx: any) => (
    <span
      className={`badge ${styles['badge-grey']} fw-normal label`}
      data-testid="data-status"
    >
      {cntx.row.original.secondaryStatus}
    </span>
  ),
  width: 5,
  sortingEnabled: true,
  direction:
    sortedColumn?.name === 'secondaryStatus' ? sortedColumn.direction : null,
});

const progressCell = (sortedColumn: SortedColumn) => ({
  name: 'progress',
  title: 'Progress Completed',
  cell: (cntx: any) => {
    const percentage = Math.round(
      (!cntx.row.original.progress ? 0 : cntx.row.original.progress / 100) *
        100,
    );
    return (
      <div className="pe-3 d-flex justify-content-between align-items-center">
        <div className={`${styles.outer}`}>
          <div style={{ width: `${percentage}%` }} className={styles.inner} />
        </div>
        <div className="label text-end" data-testid="data-percent">
          {percentage}%
        </div>
      </div>
    );
  },
  width: 10,
  sortingEnabled: true,
  direction: sortedColumn?.name === 'progress' ? sortedColumn.direction : null,
});

const orderDateCell = (sortedColumn: SortedColumn) => ({
  name: 'orderDate',
  title: 'Order Date',
  cell: (cntx: any) => (
    <div className={` fw-normal label`} data-testid="data-status">
      {PMIFormatDate(cntx.row.original.orderDate)}
    </div>
  ),
  width: 8,
  sortingEnabled: true,
  direction: sortedColumn?.name === 'orderDate' ? sortedColumn.direction : null,
});

const eventDateCell = (sortedColumn: SortedColumn) => ({
  name: 'eventDateUTC',
  title: 'Exam Date',
  cell: (cntx: any) => (
    <div className={` fw-normal label`} data-testid="data-status">
      {cntx.row.original.eventDateUTC
        ? PMIFormatDate(cntx.row.original.eventDateUTC)
        : '-'}
    </div>
  ),
  width: 8,
  sortingEnabled: true,
  direction:
    sortedColumn?.name === 'eventDateUTC' ? sortedColumn.direction : null,
});

const getColumns = (
  sortedColumn: SortedColumn,
  type: string,
  isInternalAdmin: boolean,
): SortedColumn[] => {
  if (type === ProductTypes.onlineCourse) {
    return [
      nameCell(sortedColumn, isInternalAdmin),
      orderIdCell(),
      secondaryStatusCell(sortedColumn, type),
      progressCell(sortedColumn),
      orderDateCell(sortedColumn),
    ];
  }

  if (type === ProductTypes.certification) {
    return [
      nameCell(sortedColumn, isInternalAdmin),
      orderIdCell(),
      secondaryStatusCell(sortedColumn, type),
      orderDateCell(sortedColumn),
      eventDateCell(sortedColumn),
    ];
  }

  return [
    nameCell(sortedColumn, isInternalAdmin),
    orderIdCell(),
    secondaryStatusCell(sortedColumn, type),
    orderDateCell(sortedColumn),
  ];
};

export default function RegisteredUsersTable({
  list,
  onPageChange,
  page,
  totalPages,
  onPageSizeChange,
  pageSize,
  onSort,
  sortedColumn,
  type,
  isInternalAdmin,
}: RegisteredUsersTableProps) {
  const columns = useMemo(
    () => getColumns(sortedColumn, type, isInternalAdmin),
    [sortedColumn, type, isInternalAdmin],
  );
  return (
    <ResponsiveTable
      data={list}
      paginationProps={{
        page,
        totalPages,
        onPageChange,
        onPageSizeChange,
        pageSize,
        showPageSizes: !!list.length,
        globalPageSize: true,
      }}
      columns={columns}
      showPagination={list?.length > 0}
      onSort={onSort}
    />
  );
}
